import type { DiagnosisResult, Sex } from '@/types/app'
import {
  KartenFilterUserGeneration,
  Prefecture,
} from '@/types/schema.generated'
import type {
  Channel,
  InputMaybe,
  JpyRange,
  KartenFilter,
} from '@/types/schema.generated'
import { convertSexToGqlGender } from './diagnosis'
import type { Child } from './diagnosis'

type UserID = number
export type KarteID = number

export type KarteUser = {
  id: UserID
  nickname: string
  diagnosisResult: DiagnosisResult
  yearOfBirth: number
  sex: Sex
  livingPrefecture: string
  address?: {
    prefecture: string
    city: string
  }
  annualIncome: number
  partnerAnnualIncome: Maybe<number>
  saving: number
  investment: number
  monthlyInsurance: number
  hasPartner: boolean
  hasChild: boolean
  children: Child[]
  isHighMatchingPotential?: boolean
  numberOfMatch?: number
  karte: Karte
}

export type Karte = {
  id: KarteID
  userComment?: UserKarteComment
  plannerComment?: PlannerKarteComment
  plannerCommentCount: number
  topics?: UserKarteTopics
  sameBranchPlannerCommented: boolean
  startedAt?: Date
  channel: Channel
  modifiedAt: Date
}

type UserKarteComment = {
  value: string
  noIdea: boolean
}

type PlannerKarteComment = string

type UserKarteTopics = {
  values: KarteTopic[]
  noIdea: boolean
}

type KarteTopic = {
  id: number
  name: string
}

export type KarteUserFilteringRule = {
  annualIncome: Range
  partnerAnnualIncome: Range
  saving: Range
  investment: Range
  monthlyInsuranceFee: Range
  totalAssets: Range
  loanBalance: Range
  generation: Range[]
  prefecture: string | null
  city: string | null
  sex: Sex | null
  hasPartner: boolean | null
  hasChild: boolean | null
  hasAnswered: boolean | null
  hasConcern: boolean | null
  channel: string | null
  jobIds: number[]
}

export const convertFilteringRuleToGqlInputType = (
  rule: Maybe<KarteUserFilteringRule>
): InputMaybe<KartenFilter> => {
  if (rule === undefined) {
    return null
  }

  const addresses = (() => {
    if (rule.city) {
      return [rule.city as JISX0402]
    }
    if (rule.prefecture) {
      return [rule.prefecture as JISX0402]
    }
    return []
  })()

  return {
    annualIncomeRange: convertRangeToGqlJpyRange(rule.annualIncome),
    channels: rule.channel !== null ? [rule.channel as Channel] : null,
    gender: rule.sex ? convertSexToGqlGender(rule.sex) : null,
    generation: convertGenerationRangesToGqlGenerations(rule.generation),
    hasAlreadyBeenCommentedByMe: rule.hasAnswered ?? null,
    hasChild: rule.hasChild ?? null,
    hasPartner: rule.hasPartner ?? null,
    savingRange: convertRangeToGqlJpyRange(rule.saving),
    investmentRange: convertRangeToGqlJpyRange(rule.investment),
    totalAssetsRange: convertRangeToGqlJpyRange(rule.totalAssets),
    loanBalanceRange: convertRangeToGqlJpyRange(rule.loanBalance),
    addresses,
    monthlyInsuranceRange: convertRangeToGqlJpyRange(rule.monthlyInsuranceFee),
    partnerAnnualIncomeRange: convertRangeToGqlJpyRange(
      rule.partnerAnnualIncome
    ),
    hasUserComment: rule.hasConcern ?? null,
    jobIDs:
      rule.jobIds.length > 0
        ? rule.jobIds.map(value => value.toString())
        : null,
  }
}

export type Range = {
  min: number | null
  max: number | null
}

export type KarteCommentTemplate = {
  id: string
  label: string
  body: string
  selections: string[]
}

const jisx0402ToGqlPrefecture: Record<string, Prefecture> = {
  '010006': Prefecture.Hokkaido,
  '020001': Prefecture.Aomori,
  '030007': Prefecture.Iwate,
  '040002': Prefecture.Miyagi,
  '050008': Prefecture.Akita,
  '060003': Prefecture.Yamagata,
  '070009': Prefecture.Fukushima,
  '080004': Prefecture.Ibaraki,
  '090000': Prefecture.Tochigi,
  '100005': Prefecture.Gunma,
  '110001': Prefecture.Saitama,
  '120006': Prefecture.Chiba,
  '130001': Prefecture.Tokyo,
  '140007': Prefecture.Kanagawa,
  '150002': Prefecture.Niigata,
  '160008': Prefecture.Toyama,
  '170003': Prefecture.Ishikawa,
  '180009': Prefecture.Fukui,
  '190004': Prefecture.Yamanashi,
  '200000': Prefecture.Nagano,
  '210005': Prefecture.Gifu,
  '220001': Prefecture.Shizuoka,
  '230006': Prefecture.Aichi,
  '240001': Prefecture.Mie,
  '250007': Prefecture.Shiga,
  '260002': Prefecture.Kyoto,
  '270008': Prefecture.Osaka,
  '280003': Prefecture.Hyogo,
  '290009': Prefecture.Nara,
  '300004': Prefecture.Wakayama,
  '310000': Prefecture.Tottori,
  '320005': Prefecture.Shimane,
  '330001': Prefecture.Okayama,
  '340006': Prefecture.Hiroshima,
  '350001': Prefecture.Yamaguchi,
  '360007': Prefecture.Tokushima,
  '370002': Prefecture.Kagawa,
  '380008': Prefecture.Ehime,
  '390003': Prefecture.Kochi,
  '400009': Prefecture.Fukuoka,
  '410004': Prefecture.Saga,
  '420000': Prefecture.Nagasaki,
  '430005': Prefecture.Kumamoto,
  '440001': Prefecture.Oita,
  '450006': Prefecture.Miyazaki,
  '460001': Prefecture.Kagoshima,
  '470007': Prefecture.Okinawa,
}

export const convertJisx0402CodeToGqlPrefecture = (
  code: string
): Maybe<Prefecture> => jisx0402ToGqlPrefecture[code]

const convertRangeToGqlJpyRange = (range: Range): InputMaybe<JpyRange> => {
  const min = range.min === 0 ? null : range.min
  const max = range.max === Number.MAX_SAFE_INTEGER ? null : range.max
  if (min === null && max === null) {
    return null
  }
  return { min, max }
}

const convertGenerationRangesToGqlGenerations = (
  ranges: Range[]
): KartenFilterUserGeneration[] =>
  ranges.map(({ min: lowerLimit }) => {
    switch (lowerLimit) {
      case 60:
        return KartenFilterUserGeneration.Gen_60SOrUpper
      case 50:
        return KartenFilterUserGeneration.Gen_50S
      case 40:
        return KartenFilterUserGeneration.Gen_40S
      case 30:
        return KartenFilterUserGeneration.Gen_30S
    }
    return KartenFilterUserGeneration.Gen_20SOrLower
  })
